import { v4 as uuid } from 'uuid';
import { globalEndpoints } from 'endpoints/global';
import { CLIENT_ERROR_ID } from 'utility/constants';
import { mmurApi } from 'utility/axios_config';
import { SET_SSO_AUTHENTICATED } from 'redux/types/security';
import {
  ADD_MESSAGE,
  CLEAR_MESSAGES,
  CLEAR_MODAL,
  END_WAIT,
  REMOVE_MESSAGE,
  RENDER_MODAL,
  RESET_STATE,
  SET_PANEL_DISPLAY,
  SET_SERVER_ALIAS,
  SET_SERVER_VERSION,
  WAIT,
  SET_DEBUG_OVERLAY_ENABLED,
  SET_PRIMARY_PANEL_VIEW,
  SET_SECONDARY_PANEL_VIEW,
  CLEAR_PRIMARY_PANEL,
  CLEAR_SECONDARY_PANEL,
  SET_SESSION_TIMEOUT,
  SET_SESSION_WARNING_TIMEOUT,
  Message,
  SET_POSTLOGIN_SCREEN_ROUTE,
} from '../types/global';
import { AppDispatch } from '../types/general';
import { Panel } from '../../utility/enumerations';

export function resetState() {
  return function (dispatch: AppDispatch) {
    return new Promise<void>((resolve) => {
      dispatch({
        type: RESET_STATE,
      });

      resolve();
    });
  };
}

export function wait() {
  return function (dispatch: AppDispatch) {
    dispatch({ type: WAIT });
  };
}

export function setSsoAuthenticated(dispatch: AppDispatch, ssoAuthenticated: boolean) {
  return new Promise<void>((resolve: () => void) => {
    dispatch({ type: SET_SSO_AUTHENTICATED, payload: ssoAuthenticated });
    resolve();
  });
}

export function endWait() {
  return function (dispatch: AppDispatch) {
    dispatch({ type: END_WAIT });
  };
}

export function renderModal(modal) {
  return function (dispatch: AppDispatch) {
    return new Promise<void>((resolve: () => void) => {
      dispatch({
        type: RENDER_MODAL,
        payload: modal,
      });

      resolve();
    });
  };
}

export function clearModal() {
  return function (dispatch: AppDispatch) {
    return new Promise<void>((resolve: () => void) => {
      dispatch({
        type: CLEAR_MODAL,
      });

      resolve();
    });
  };
}

export function addMessage(message: Message) {
  message.timeout = message.timeout === 0 ? 0 : message.timeout || 5000;
  message.id = message.id || Number(`0x${uuid().replace(/-/g, '').slice(0, 8)}`);
  return function (dispatch: AppDispatch) {
    dispatch({
      type: ADD_MESSAGE,
      payload: message,
    });
    if (message.timeout !== 0) {
      setTimeout(() => {
        dispatch({
          type: REMOVE_MESSAGE,
          payload: message.id,
        });
      }, message.timeout);
    }
  };
}

export function removeMessage(messageId) {
  return function (dispatch: AppDispatch) {
    dispatch({
      type: REMOVE_MESSAGE,
      payload: messageId,
    });
  };
}

export function clearMessages() {
  return function (dispatch: AppDispatch) {
    dispatch({
      type: CLEAR_MESSAGES,
    });
  };
}

export function setServerAlias(alias: string) {
  return function (dispatch: AppDispatch) {
    dispatch({
      type: SET_SERVER_ALIAS,
      payload: alias,
    });
  };
}

export function setServerVersion(version) {
  return function (dispatch: AppDispatch) {
    dispatch({
      type: SET_SERVER_VERSION,
      payload: version,
    });
  };
}

export function setPanelDisplay(panelDisplayStates) {
  return function (dispatch: AppDispatch) {
    dispatch({
      type: SET_PANEL_DISPLAY,
      payload: panelDisplayStates,
    });
  };
}

export function setDebugOverlayEnabled(enabled: boolean) {
  return function (dispatch: AppDispatch) {
    dispatch({
      type: SET_DEBUG_OVERLAY_ENABLED,
      payload: enabled,
    });
  };
}

export function setPrimaryPanelComponents(components: any) {
  return function (dispatch: AppDispatch) {
    dispatch({
      type: SET_PRIMARY_PANEL_VIEW,
      payload: components,
    });
  };
}

export function setSecondaryPanelComponents(components) {
  return function (dispatch: AppDispatch) {
    dispatch({
      type: SET_SECONDARY_PANEL_VIEW,
      payload: components,
    });
  };
}

export function clearPrimaryPanel() {
  return function (dispatch) {
    dispatch({
      type: CLEAR_PRIMARY_PANEL,
    });
  };
}

export function clearSecondaryPanel() {
  return function (dispatch) {
    dispatch({
      type: CLEAR_SECONDARY_PANEL,
    });
  };
}

export interface MountInstructions {
  viewPath: string,
  viewProps: any,
  focusView: boolean,
  targetPanel: any,
  panelLayout: any,
  clear: boolean,
  overrideLocation: string,
  params: any
}

export function mountView(mountInstructions: MountInstructions) {
  const {
    viewPath,
    viewProps = {},
    focusView = false,
    targetPanel,
    panelLayout = null,
    clear = false,
    overrideLocation,
    params = {},
  } = mountInstructions;
  const view = {
    viewPath, viewProps, focusView, overrideLocation, params,
  };
  return function (dispatch) {
    if (targetPanel === Panel.Primary) {
      dispatch({ type: SET_PRIMARY_PANEL_VIEW, payload: view });

      if (clear === true) {
        dispatch({ type: CLEAR_SECONDARY_PANEL });
      }
    } else if (targetPanel === Panel.Secondary) {
      if (clear === true) {
        dispatch({ type: CLEAR_SECONDARY_PANEL });
      }
      dispatch({ type: SET_SECONDARY_PANEL_VIEW, payload: view });
    }

    if (panelLayout) {
      dispatch({ type: SET_PANEL_DISPLAY, payload: panelLayout });
    }
  };
}

export function logClientError(errorData) {
  const url = globalEndpoints.logClientError();

  const data = {
    exceptionType: CLIENT_ERROR_ID,
    exception: errorData.error,
    route: errorData.routeOrStack,
    component: errorData.component
  };

  mmurApi.post(url, data);
}

export function setSessionTimeoutInSeconds(sessionTimeOutInSeconds: string) {
  return function (dispatch: AppDispatch) {
    dispatch({
      type: SET_SESSION_TIMEOUT,
      payload: sessionTimeOutInSeconds,
    });
  };
}

export function setSessionWarningTimeoutInSeconds(sessionWarningTimeoutInSeconds: string) {
  return function (dispatch: AppDispatch) {
    dispatch({
      type: SET_SESSION_WARNING_TIMEOUT,
      payload: sessionWarningTimeoutInSeconds,
    });
  };
}

export function setPostLoginRoute(route: string) {
  return function (dispatch: AppDispatch) {
    dispatch({
      type: SET_POSTLOGIN_SCREEN_ROUTE,
      payload: route,
    });
  };
}
